<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaFormField from '../../../base/GaiaFormField.vue';
import GaiaInput from '../../../base/inputs/GaiaInput.vue';
import GaiaText from '../../../base/GaiaText.vue';
import GaiaCalendarRemindersStep from './GaiaCalendarRemindersStep.vue';
import GaiaUserSelect from '../../people/GaiaUserSelect.vue';
import type { CalendarWizardForm, CalendarWizardSeeds } from '@/composables/useCalendarWizard';

const props = defineProps({
  form: {
    type: Object as PropType<CalendarWizardForm>,
    required: true,
  },
  /** Already-linked records, so a picker can label a uid it has not searched. */
  seeds: {
    type: Object as PropType<CalendarWizardSeeds>,
    required: true,
  },
  /**
   * The user has tried to leave this step. Until then a missing name is just
   * an empty field, not a mistake worth colouring red.
   */
  touched: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ 'update:form': [form: CalendarWizardForm] }>();

function patch(changes: Partial<CalendarWizardForm>): void {
  emit('update:form', { ...props.form, ...changes });
}

const nameError = computed(() =>
  props.touched && props.form.name.trim().length === 0
    ? 'Give the calendar a name before continuing'
    : undefined
);

/**
 * One Receivers field for both kinds of receiver: workspace users (by uid) and
 * addresses typed by hand (`tag`). A uid never contains an @, so that is how
 * the value splits back into `receiverUids` and `receiverEmails`.
 */
const receiverValue = computed(() => [...props.form.receiverUids, ...props.form.receiverEmails]);

// The backend validates the addresses and rejects the save with the bad one.
function updateReceivers(value: string[]): void {
  patch({
    receiverUids: value.filter(entry => !entry.includes('@')),
    receiverEmails: value.filter(entry => entry.includes('@')),
  });
}
</script>

<template>
  <n-flex vertical :size="18" :wrap="false">
    <gaia-form-field label="Name" required :error="nameError">
      <gaia-input
        :value="props.form.name"
        size="medium"
        placeholder="e.g. NDA renewals"
        :status="nameError ? 'error' : undefined"
        style="width: 100%"
        @update:value="(value: string) => patch({ name: value })"
      />
    </gaia-form-field>

    <n-flex vertical :size="4" :wrap="false" :style="{ marginTop: '6px' }">
      <gaia-text variant="label" size="micro" tone="faint">Receivers</gaia-text>
      <gaia-text size="meta" tone="subtle">
        Receivers get the reminder emails — pick workspace users or type any email address.
      </gaia-text>
    </n-flex>

    <gaia-form-field label="Receivers" hint="Press Enter after each typed address">
      <gaia-user-select
        multiple
        clearable
        tag
        :value="receiverValue"
        :seed-users="props.seeds.receivers"
        placeholder="Search users or type an email address"
        style="width: 100%"
        @update:value="updateReceivers"
      />
    </gaia-form-field>

    <gaia-text variant="label" size="micro" tone="faint" :style="{ marginTop: '6px' }">
      Reminders
    </gaia-text>
    <gaia-calendar-reminders-step
      :form="props.form"
      @update:form="(next: CalendarWizardForm) => emit('update:form', next)"
    />
  </n-flex>
</template>
